import React, { useState, useEffect, useRef } from 'react';
import FeaturedContent from '../components/FeaturedContent';

// Custom CSS from the original HTML injected directly to maintain 100% fidelity
const customCSS = `
/* ── RESET & BASE ── */
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
:root {
  --bg-main: #0a0914;
  --bg-card: #151325;
  --bg-elevated: #1a1830;
  --bg-hover: #221f38;
  --text-primary: #f8fafc;
  --text-secondary: #94a3b8;
  --text-muted: #64748b;
  --border-light: rgba(255, 255, 255, 0.08);
  --border-strong: rgba(255, 255, 255, 0.15);
  --accent: #F09E07;
  --accent-hover: #ffb52a;
  --accent-glow: rgba(240, 158, 7, 0.25);
  --accent-soft: rgba(240, 158, 7, 0.1);
  --success: #10b981;
  --danger: #ef4444;
  --radius-sm: 8px; --radius-md: 12px; --radius-lg: 20px; --radius-xl: 32px;
}

[data-theme="light"] {
  --bg-main: #f8fafc;
  --bg-card: #ffffff;
  --bg-elevated: #f1f5f9;
  --bg-hover: #e2e8f0;
  --text-primary: #0f172a;
  --text-secondary: #475569;
  --text-muted: #94a3b8;
  --border-light: rgba(0, 0, 0, 0.08);
  --border-strong: rgba(0, 0, 0, 0.15);
}

html { scroll-behavior: smooth; }
body {
  font-family: 'Inter', sans-serif;
  background-color: var(--bg-main);
  color: var(--text-primary);
  line-height: 1.6;
  overflow-x: hidden;
  transition: background-color 0.4s ease, color 0.4s ease;
}

::-webkit-scrollbar { width: 8px; }
::-webkit-scrollbar-track { background: var(--bg-main); }
::-webkit-scrollbar-thumb { background: var(--bg-hover); border-radius: 4px; }

/* ── TYPOGRAPHY ── */
h1, h2, h3, h4 { line-height: 1.2; letter-spacing: -0.02em; }
.text-gradient {
  background: linear-gradient(135deg, var(--accent), #ffc947);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* ── LAYOUT & UTILS ── */
.container { max-width: 1200px; margin: 0 auto; padding: 0 5%; }
.section { padding: 120px 0; position: relative; }
.text-center { text-align: center; }
.reveal { opacity: 0; transform: translateY(30px); transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1); }
.reveal.active { opacity: 1; transform: translateY(0); }

/* ── ANIMATED PEN CURVE ── */
.relative-inline { position: relative; display: inline-block; }
.animated-curve {
  position: absolute; left: 0; bottom: -8px; width: 100%; height: 12px;
  stroke: var(--accent); stroke-width: 3; fill: none; stroke-linecap: round;
  stroke-dasharray: 100; stroke-dashoffset: 100;
  transition: stroke-dashoffset 1s cubic-bezier(0.65, 0, 0.35, 1);
  pointer-events: none; z-index: -1;
}
.reveal.active .animated-curve { stroke-dashoffset: 0; }

/* ── BUTTONS ── */
.btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  font-weight: 700; text-decoration: none; border-radius: var(--radius-md);
  transition: all 0.3s ease; cursor: pointer; border: none; font-family: inherit;
}
.btn:active { transform: scale(0.98); }
.btn-primary {
  background: linear-gradient(135deg, var(--accent), var(--accent-hover));
  color: #000; padding: 16px 36px; font-size: 1.125rem;
  box-shadow: 0 10px 30px var(--accent-glow); position: relative; overflow: hidden;
}
.btn-primary::after {
  content: ''; position: absolute; top: 0; left: -100%; width: 50%; height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
  transition: 0.5s;
}
.btn-primary:hover::after { left: 100%; }
.btn-primary:hover { transform: translateY(-3px); box-shadow: 0 15px 40px rgba(240,158,7,0.5); }
.btn-secondary {
  background: var(--bg-card); color: var(--text-primary); padding: 16px 32px; font-size: 1rem;
  border: 1px solid var(--border-strong);
}
.btn-secondary:hover { background: var(--bg-hover); transform: translateY(-2px); box-shadow: 0 10px 20px rgba(0,0,0,0.1); }

/* ── NAVBAR ── */
.navbar {
  position: fixed; top: 0; width: 100%; z-index: 1000; padding: 20px 0;
  transition: all 0.3s ease;
}
.navbar.scrolled {
  padding: 12px 0; background: rgba(10, 9, 20, 0.85);
  backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);
  border-bottom: 1px solid var(--border-light);
}
[data-theme="light"] .navbar.scrolled { background: rgba(255, 255, 255, 0.85); border-bottom: 1px solid var(--border-strong); }
.nav-container { display: flex; justify-content: space-between; align-items: center; }

.nav-left-group { display: flex; align-items: center; gap: 24px; }
.nav-back { 
  display: flex; align-items: center; gap: 6px; font-size: 0.8rem; font-weight: 700;
  color: var(--text-secondary); text-decoration: none; text-transform: uppercase; letter-spacing: 0.5px;
  transition: 0.2s; padding-right: 24px; border-right: 1px solid var(--border-strong);
}
.nav-back:hover { color: var(--accent); transform: translateX(-2px); }

.nav-brand { display: flex; align-items: center; gap: 12px; text-decoration: none; }
.logo-icon {
  width: 44px; height: 44px; border-radius: var(--radius-md);
  background: linear-gradient(135deg, #1a1830, #100f20); border: 1px solid rgba(255,255,255,0.15);
  display: flex; align-items: center; justify-content: center; transition: 0.3s;
}
.nav-brand:hover .logo-icon { transform: scale(1.05) rotate(5deg); border-color: var(--accent); box-shadow: 0 0 15px var(--accent-glow); }
.nav-title { font-size: 1.25rem; font-weight: 900; color: var(--text-primary); }
.nav-subtitle { font-size: 0.65rem; font-weight: 700; color: var(--accent); text-transform: uppercase; letter-spacing: 1px; display: block; margin-top: -2px; }

.nav-links { display: flex; align-items: center; gap: 32px; }
.nav-link { color: var(--text-secondary); font-size: 0.9rem; font-weight: 600; text-decoration: none; transition: 0.2s; }
.nav-link:hover { color: var(--text-primary); }
.nav-actions { display: flex; align-items: center; gap: 16px; }

.theme-toggle {
  background: var(--bg-elevated); border: 1px solid var(--border-light);
  width: 40px; height: 40px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
  color: var(--text-primary); cursor: pointer; transition: 0.3s;
}
.theme-toggle:hover { background: var(--bg-hover); }
.nav-cta {
  background: var(--accent); color: #000; padding: 10px 24px; font-weight: 800;
  border-radius: var(--radius-sm); text-decoration: none; font-size: 0.9rem; transition: 0.3s;
}
.nav-cta:hover { background: var(--accent-hover); transform: translateY(-2px); box-shadow: 0 4px 15px var(--accent-glow); }

.mobile-menu-btn { display: none; background: none; border: none; color: var(--text-primary); cursor: pointer; }
.mobile-menu { display: none; }

/* ── HERO SECTION ── */
.hero { min-height: 100vh; display: flex; align-items: center; justify-content: center; padding-top: 120px; overflow: hidden; }
.hero-glow {
  position: absolute; top: 10%; left: 50%; transform: translateX(-50%);
  width: 800px; height: 800px; background: radial-gradient(circle, var(--accent-glow) 0%, transparent 60%);
  filter: blur(60px); z-index: -1; pointer-events: none;
}
.badge {
  display: inline-flex; align-items: center; gap: 8px;
  background: var(--accent-soft); border: 1px solid var(--accent-glow);
  color: var(--accent); font-size: 0.8rem; font-weight: 700; text-transform: uppercase;
  padding: 6px 16px; border-radius: 100px; margin-bottom: 32px; letter-spacing: 1px;
}
.pulse-dot { width: 8px; height: 8px; background: var(--accent); border-radius: 50%; box-shadow: 0 0 10px var(--accent); animation: pulse 2s infinite; }
@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }

.hero h1 { font-size: clamp(2.5rem, 6vw, 4.5rem); font-weight: 900; margin-bottom: 24px; max-width: 900px; margin-inline: auto; }
.hero p { font-size: clamp(1rem, 2vw, 1.25rem); color: var(--text-secondary); max-width: 700px; margin: 0 auto 48px; }
.hero-buttons { display: flex; gap: 16px; justify-content: center; margin-bottom: 80px; }

/* ── INTERACTIVE MOCKUP ── */
.mockup-container { perspective: 1000px; max-width: 1000px; margin: 0 auto; z-index: 10; position: relative; }
.mockup-window {
  background: var(--bg-card); border: 1px solid var(--border-light); border-radius: var(--radius-lg);
  overflow: hidden; box-shadow: 0 40px 100px rgba(0,0,0,0.6), 0 0 0 1px var(--border-light);
  transform: rotateX(4deg) translateY(0); transition: transform 0.4s ease, box-shadow 0.4s ease;
}
.mockup-container:hover .mockup-window { transform: rotateX(0deg) translateY(-10px); box-shadow: 0 50px 120px rgba(0,0,0,0.7), 0 0 0 1px var(--accent-glow); }
[data-theme="light"] .mockup-window { box-shadow: 0 30px 60px rgba(0,0,0,0.1); }

.mockup-header {
  background: var(--bg-elevated); height: 48px; display: flex; align-items: center; padding: 0 16px;
  border-bottom: 1px solid var(--border-light); position: relative;
}
.mac-dots { display: flex; gap: 8px; }
.mac-dot { width: 12px; height: 12px; border-radius: 50%; }
.mac-dot.r { background: #ff5f57; } .mac-dot.y { background: #febc2e; } .mac-dot.g { background: #28c840; }
.mockup-title { position: absolute; left: 50%; transform: translateX(-50%); font-size: 0.75rem; font-weight: 600; color: var(--text-secondary); display: flex; align-items: center; gap: 8px; }

.mockup-body { display: flex; height: 400px; }
.mockup-sidebar { width: 220px; background: var(--bg-main); border-right: 1px solid var(--border-light); padding: 16px; }
.sidebar-item { padding: 10px 12px; border-radius: var(--radius-sm); font-size: 0.85rem; font-weight: 600; color: var(--text-secondary); display: flex; align-items: center; gap: 10px; margin-bottom: 4px; cursor: pointer; transition: 0.2s; }
.sidebar-item:hover { color: var(--text-primary); }
.sidebar-item.active { background: var(--accent-soft); color: var(--accent); }

.mockup-main { flex: 1; display: flex; flex-direction: column; background: var(--bg-card); }
.mockup-toolbar { height: 48px; border-bottom: 1px solid var(--border-light); display: flex; align-items: center; padding: 0 16px; gap: 8px; }
.tool-btn { background: transparent; border: none; color: var(--text-secondary); padding: 6px 12px; border-radius: 4px; font-size: 0.8rem; font-weight: 700; cursor: pointer; transition: 0.2s;}
.tool-btn:hover { background: var(--bg-hover); color: var(--text-primary); }

.mockup-content { flex: 1; padding: 32px; font-family: 'Consolas', monospace; font-size: 0.9rem; color: var(--text-primary); overflow-y: hidden; text-align: left;}
.mockup-content .h1 { color: var(--accent); font-size: 1.25rem; font-weight: 800; margin-bottom: 16px; }
.mockup-content .h2 { color: #3b82f6; font-size: 1rem; font-weight: 700; margin-bottom: 12px; margin-top: 24px; }
.cursor { display: inline-block; width: 8px; height: 16px; background: var(--accent); vertical-align: middle; animation: blink 1s infinite; }

.mockup-footer { height: 64px; background: var(--bg-elevated); border-top: 1px solid var(--border-light); display: flex; align-items: center; padding: 0 24px; gap: 24px; }
.format-toggle { display: flex; gap: 4px; background: var(--bg-main); padding: 4px; border-radius: var(--radius-sm); border: 1px solid var(--border-light); }
.format-btn { padding: 6px 16px; font-size: 0.75rem; font-weight: 700; color: var(--text-secondary); border-radius: 4px; cursor: pointer; transition: 0.2s; }
.format-btn.active { background: rgba(59, 130, 246, 0.1); color: #3b82f6; }
.export-progress { flex: 1; height: 8px; background: var(--bg-hover); border-radius: 4px; overflow: hidden; }
.progress-bar { height: 100%; width: 0%; background: linear-gradient(90deg, var(--accent), var(--success)); transition: width 0.3s; }
.export-btn { background: var(--success); color: #fff; border: none; padding: 8px 24px; border-radius: var(--radius-sm); font-weight: 800; font-size: 0.8rem; opacity: 0; transform: translateY(10px); transition: all 0.3s; }
.export-btn.show { opacity: 1; transform: translateY(0); box-shadow: 0 4px 15px rgba(16, 185, 129, 0.4);}

/* ── VIDEO DEMO SECTION ── */
.video-section { background: var(--bg-elevated); border-top: 1px solid var(--border-light); border-bottom: 1px solid var(--border-light); }
.video-wrapper { max-width: 900px; margin: 0 auto; position: relative; border-radius: var(--radius-xl); overflow: hidden; box-shadow: 0 30px 60px rgba(0,0,0,0.3); border: 1px solid var(--border-strong); background: #000; }

/* ── COMPARISON (Reality Check) ── */
.compare-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); gap: 32px; max-width: 1000px; margin: 64px auto 0; }
.compare-card { background: var(--bg-card); border: 1px solid var(--border-light); border-radius: var(--radius-xl); padding: 48px; position: relative; transition: 0.4s ease; }
.compare-card.bad { border-color: rgba(239, 68, 68, 0.2); }
.compare-card.bad:hover { border-color: rgba(239, 68, 68, 0.4); box-shadow: 0 20px 50px rgba(239, 68, 68, 0.05); transform: translateY(-5px); }
.compare-card.good { border: 2px solid var(--accent); background: linear-gradient(180deg, var(--bg-elevated) 0%, var(--bg-card) 100%); box-shadow: 0 20px 50px var(--accent-glow); transform: translateY(-10px); }
.compare-card.good:hover { box-shadow: 0 30px 60px rgba(240,158,7,0.3); transform: translateY(-15px); }

.compare-header { display: flex; align-items: center; gap: 16px; margin-bottom: 32px; padding-bottom: 24px; border-bottom: 1px solid var(--border-light); }
.compare-icon { width: 56px; height: 56px; border-radius: 50%; display: flex; align-items: center; justify-content: center; transition: 0.3s;}
.compare-card:hover .compare-icon { transform: scale(1.1); }
.bad .compare-icon { background: rgba(239, 68, 68, 0.1); color: var(--danger); }
.good .compare-icon { background: var(--accent-soft); color: var(--accent); }
.compare-title { font-size: 1.5rem; font-weight: 800; color: var(--text-primary); }
.compare-subtitle { font-size: 0.85rem; font-weight: 600; color: var(--text-secondary); text-transform: uppercase; letter-spacing: 1px; }
.good .compare-subtitle { color: var(--accent); }
.compare-list { list-style: none; }
.compare-list li { display: flex; gap: 12px; margin-bottom: 20px; font-size: 0.95rem; color: var(--text-secondary); font-weight: 500; transition: 0.2s;}
.compare-list li:hover { color: var(--text-primary); transform: translateX(5px); }
.compare-list li svg { flex-shrink: 0; width: 20px; height: 20px; margin-top: 2px; }
.bad .compare-list li svg { color: var(--danger); }
.good .compare-list li svg { color: var(--success); }

/* ── FEATURES ── */
.features-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 32px; margin-top: 64px; }
.feature-card { background: var(--bg-elevated); border: 1px solid var(--border-light); border-radius: var(--radius-lg); padding: 40px; transition: 0.3s; }
.feature-card:hover { transform: translateY(-8px); border-color: var(--border-strong); box-shadow: 0 20px 40px rgba(0,0,0,0.2); }
.feature-icon { width: 60px; height: 60px; border-radius: 16px; background: var(--bg-main); border: 1px solid var(--border-strong); display: flex; align-items: center; justify-content: center; color: var(--accent); margin-bottom: 24px; transition: 0.3s;}
.feature-card:hover .feature-icon { background: var(--accent-soft); border-color: var(--accent); transform: scale(1.1) rotate(5deg); }
.feature-card h3 { font-size: 1.25rem; font-weight: 800; margin-bottom: 12px; }
.feature-card p { font-size: 0.95rem; color: var(--text-secondary); }

/* ── PRICING ── */
.pricing-section { background: var(--bg-elevated); overflow: hidden; }
.pricing-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(350px, 1fr)); gap: 40px; max-width: 1000px; margin: 64px auto 0; align-items: center; }
.price-card { background: var(--bg-card); border: 1px solid var(--border-light); border-radius: var(--radius-xl); padding: 48px; position: relative; transition: 0.3s; }
.price-card.premium { border: 2px solid var(--accent); background: linear-gradient(180deg, var(--bg-main) 0%, var(--bg-card) 100%); transform: scale(1.05); z-index: 2; box-shadow: 0 30px 60px var(--accent-glow); }
.price-card.premium:hover { transform: scale(1.05) translateY(-5px); box-shadow: 0 40px 80px rgba(240,158,7,0.35); }
.price-badge { position: absolute; top: -14px; left: 50%; transform: translateX(-50%); background: linear-gradient(90deg, var(--accent), #ffc947); color: #000; font-size: 0.75rem; font-weight: 900; padding: 6px 20px; border-radius: 100px; text-transform: uppercase; letter-spacing: 1px; }
.price-header { margin-bottom: 32px; }
.price-header h3 { font-size: 1.5rem; font-weight: 800; margin-bottom: 8px; }
.price-header p { font-size: 0.9rem; color: var(--text-secondary); min-height: 44px; }
.price-amount { display: flex; align-items: flex-start; gap: 4px; margin-bottom: 32px; }
.currency { font-size: 1.5rem; font-weight: 800; color: var(--text-muted); margin-top: 8px; }
.amount { font-size: 4rem; font-weight: 900; line-height: 1; letter-spacing: -0.05em; }
.premium .amount { color: var(--accent); }
.price-list { list-style: none; margin-bottom: 40px; }
.price-list li { display: flex; align-items: flex-start; gap: 12px; margin-bottom: 16px; font-size: 0.95rem; color: var(--text-secondary); font-weight: 500; }
.price-list li svg { flex-shrink: 0; width: 20px; height: 20px; color: var(--success); }
.price-list li.strike { opacity: 0.5; text-decoration: line-through; }
.price-list li.strike svg { color: var(--text-muted); }
.premium .highlight-item { background: var(--accent-soft); color: var(--text-primary); padding: 12px 16px; border-radius: var(--radius-md); font-weight: 700; border: 1px solid var(--accent-glow); margin-bottom: 24px; }
.premium .highlight-item svg { color: var(--accent); }

/* ── SCREENSHOT DOWNLOAD SECTION ── */
.download-section {
  background-color: var(--bg-elevated); 
  padding: 100px 5%; position: relative; overflow: hidden; border-top: 1px solid var(--border-light); border-bottom: 1px solid var(--border-light);
}
.dl-bg-glow {
  position: absolute; bottom: -20%; left: 50%; transform: translateX(-50%);
  width: 800px; height: 600px; background: radial-gradient(circle, var(--accent-glow) 0%, transparent 60%);
  filter: blur(80px); pointer-events: none;
}
.dl-container { max-width: 800px; margin: 0 auto; text-align: center; position: relative; z-index: 10; }
.dl-badge {
  display: inline-block; border: 1px solid var(--accent-glow); color: var(--accent); background: var(--accent-soft);
  font-size: 0.75rem; font-weight: 800; text-transform: uppercase; letter-spacing: 2px;
  padding: 6px 20px; border-radius: 100px; margin-bottom: 30px;
}
.dl-title { font-size: clamp(2.5rem, 5vw, 4rem); font-weight: 900; color: var(--text-primary); margin-bottom: 20px; letter-spacing: -1px; }
.dl-subtitle { font-size: 1.1rem; color: var(--text-secondary); margin-bottom: 48px; }
.dl-btn {
  display: inline-flex; align-items: center; gap: 12px;
  background: var(--accent); color: #000000; font-weight: 900; font-size: 1.1rem;
  padding: 20px 48px; border-radius: 100px; text-decoration: none;
  box-shadow: 0 10px 40px rgba(240,158,7,0.4); transition: all 0.3s ease; margin-bottom: 40px; cursor: pointer; border: none; font-family: inherit;
}
.dl-btn:hover { transform: translateY(-3px) scale(1.02); box-shadow: 0 15px 50px rgba(240,158,7,0.6); background: #ffb52a; }
.dl-specs {
  display: inline-flex; align-items: center; gap: 24px; flex-wrap: wrap; justify-content: center;
  border: 1px solid var(--border-light); padding: 12px 32px; border-radius: 100px;
  background: var(--bg-card);
}
.dl-spec-item { display: flex; align-items: center; gap: 8px; font-size: 0.9rem; font-weight: 600; color: var(--text-secondary); }
.dl-spec-item svg { width: 18px; height: 18px; }
.dl-spec-dot { width: 4px; height: 4px; background: var(--border-strong); border-radius: 50%; }

/* ── MODALS ── */
.modal { position: fixed; inset: 0; background: rgba(0,0,0,0.8); backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; z-index: 2000; opacity: 0; visibility: hidden; transition: 0.3s; }
.modal.active { opacity: 1; visibility: visible; }
.modal-box { background: var(--bg-card); border: 1px solid var(--border-light); width: 100%; max-width: 420px; border-radius: var(--radius-xl); padding: 40px; position: relative; transform: scale(0.95) translateY(20px); transition: 0.3s; box-shadow: 0 40px 80px rgba(0,0,0,0.5); }
.modal.active .modal-box { transform: scale(1) translateY(0); }
.close-modal { position: absolute; top: 20px; right: 20px; background: var(--bg-elevated); border: none; width: 36px; height: 36px; border-radius: 50%; color: var(--text-secondary); cursor: pointer; display: flex; align-items: center; justify-content: center; transition: 0.2s; }
.close-modal:hover { background: var(--bg-hover); color: var(--text-primary); }

/* ── FOOTER ── */
.footer { padding: 80px 0 40px; background: var(--bg-main); border-top: 1px solid var(--border-light); }
.footer-grid { display: grid; grid-template-columns: 2fr 1fr 1fr; gap: 64px; margin-bottom: 64px; }
.footer-brand p { color: var(--text-secondary); font-size: 0.9rem; margin-top: 16px; max-width: 300px; }
.footer-title { font-size: 0.85rem; font-weight: 800; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 24px; color: var(--text-primary); }
.footer-links { list-style: none; }
.footer-links li { margin-bottom: 12px; }
.footer-links a { color: var(--text-secondary); text-decoration: none; font-size: 0.95rem; font-weight: 500; transition: 0.2s; }
.footer-links a:hover { color: var(--accent); transform: translateX(4px); display: inline-block;}
.footer-bottom { border-top: 1px solid var(--border-light); padding-top: 32px; display: flex; justify-content: space-between; align-items: center; color: var(--text-muted); font-size: 0.85rem; }

/* ── 📱 ADVANCED MOBILE & TABLET MEDIA QUERIES ── */
@media (max-width: 1024px) {
  .pricing-grid { max-width: 800px; }
  .price-card.premium { transform: scale(1); }
  .price-card.premium:hover { transform: translateY(-5px); }
  .footer-grid { grid-template-columns: repeat(2, 1fr); gap: 40px; }
}

@media (max-width: 768px) {
  .section { padding: 80px 0; }
  .nav-left-group { gap: 12px; }
  .nav-back span { display: none; } 
  .nav-back { padding-right: 12px; }
  .nav-links, .nav-actions { display: none; }
  .mobile-menu-btn { display: block; }
  
  .mobile-menu { 
    display: none; 
    flex-direction: column; position: absolute; top: 100%; left: 0; width: 100%; 
    background: var(--bg-card); padding: 24px; border-bottom: 1px solid var(--border-light); 
    box-shadow: 0 20px 40px rgba(0,0,0,0.2); 
  }
  .mobile-menu.active { display: flex; } 
  
  .hero { padding-top: 120px; }
  .hero-buttons { flex-direction: column; width: 100%; max-width: 350px; margin: 0 auto 60px; }
  .hero-buttons .btn { width: 100%; }

  .mockup-body { flex-direction: column; height: auto; }
  .mockup-sidebar { width: 100%; border-right: none; border-bottom: 1px solid var(--border-light); display: flex; flex-wrap: nowrap; overflow-x: auto; padding: 8px 12px; }
  .sidebar-item { margin-bottom: 0; margin-right: 8px; white-space: nowrap; padding: 6px 10px; font-size: 0.75rem; flex-shrink: 0; }
  .mockup-toolbar { overflow-x: auto; padding: 0 12px; height: 40px; }
  .tool-btn { padding: 4px 8px; font-size: 0.75rem; flex-shrink: 0; }
  .mockup-content { padding: 16px; min-height: 200px; font-size: 0.8rem; }
  .mockup-content .h1 { font-size: 1rem; margin-bottom: 12px; }
  .mockup-content .h2 { font-size: 0.85rem; margin-bottom: 8px; margin-top: 16px; }
  .mockup-footer { height: auto; min-height: 50px; padding: 8px 12px; gap: 8px; flex-wrap: wrap; justify-content: space-between; }
  .export-btn { padding: 6px 12px; font-size: 0.75rem; }
  
  .mockup-window { transform: none !important; box-shadow: 0 10px 30px rgba(0,0,0,0.3) !important; }
  .mockup-container:hover .mockup-window { transform: none !important; }

  .compare-grid, .features-grid, .pricing-grid { 
    grid-template-columns: 1fr !important; 
    gap: 24px; 
  }
  
  .compare-card, .feature-card, .price-card { padding: 32px 24px; }
  
  .compare-header { flex-direction: row; text-align: left; gap: 16px; margin-bottom: 24px; padding-bottom: 20px; }
  .compare-icon { width: 48px; height: 48px; }
  .compare-icon svg { width: 20px; height: 20px; }
  .compare-title { font-size: 1.25rem; }
  .compare-subtitle { font-size: 0.8rem; }
  .compare-list li { font-size: 0.9rem; gap: 10px; margin-bottom: 16px; }
  .compare-list li svg { width: 18px; height: 18px; margin-top: 2px; }

  .feature-icon { width: 48px; height: 48px; margin-bottom: 16px; border-radius: 12px;}
  .feature-icon svg { width: 24px; height: 24px; }
  .feature-card h3 { font-size: 1.15rem; margin-bottom: 8px; }
  .feature-card p { font-size: 0.9rem; }

  .price-badge { font-size: 0.65rem; padding: 6px 16px; top: -12px; }
  .price-header h3 { font-size: 1.25rem; }
  .price-header p { font-size: 0.85rem; margin-bottom: 24px; min-height: auto; }
  .price-amount { margin-bottom: 24px; align-items: flex-start; justify-content: flex-start;}
  .amount { font-size: 3rem; }
  .currency { font-size: 1.25rem; margin-top: 6px;}
  .price-list li { font-size: 0.9rem; gap: 10px; margin-bottom: 12px; }
  .price-list li svg { width: 18px; height: 18px; }
  .premium .highlight-item { padding: 12px; font-size: 0.85rem; text-align: left; margin-bottom: 20px; }
  .price-card .btn { padding: 14px 20px; font-size: 0.95rem; }
  
  .dl-specs { flex-direction: column; gap: 16px; padding: 20px; border-radius: 20px; }
  .dl-spec-dot { display: none; }
  .dl-btn { width: 100%; justify-content: center; }

  .footer-grid { grid-template-columns: 1fr; gap: 40px; }
  .footer-bottom { flex-direction: column; text-align: center; gap: 12px; }
}

@media (max-width: 480px) {
  .hero h1 { font-size: 2.2rem; }
  .dl-title { font-size: 2.5rem; }
  .badge, .dl-badge { font-size: 0.7rem; padding: 6px 12px; }
  .btn-primary, .btn-secondary, .dl-btn { padding: 14px 24px; font-size: 1rem; }
  .nav-subtitle { display: none; }
  .nav-title { font-size: 1.1rem; }
}

/* ── TESTIMONIALS ── */
.testi-card { background:var(--bg-elevated); border:1px solid var(--border-light); border-radius:var(--radius-lg); padding:32px; transition:.3s; }
.testi-card:hover { transform:translateY(-6px); border-color:var(--border-strong); box-shadow:0 16px 40px rgba(0,0,0,.2); }
.testi-stars { color:var(--accent); font-size:1.1rem; letter-spacing:2px; margin-bottom:16px; }
.testi-quote { font-size:.97rem; color:var(--text-secondary); line-height:1.75; margin-bottom:24px; font-style:italic; }
.testi-author { display:flex; align-items:center; gap:14px; }
.testi-avatar { width:44px; height:44px; border-radius:50%; background:linear-gradient(135deg,var(--accent),#ffc947); display:flex; align-items:center; justify-content:center; font-weight:900; font-size:1.1rem; color:#000; flex-shrink:0; }
.testi-name { display:block; font-weight:800; font-size:.95rem; color:var(--text-primary); }
.testi-role { display:block; font-size:.8rem; color:var(--text-muted); margin-top:2px; }

/* ── FAQ ── */
.faq-item { border:1px solid var(--border-light); border-radius:var(--radius-md); margin-bottom:12px; overflow:hidden; transition:.2s; }
.faq-item:hover { border-color:var(--border-strong); }
.faq-item[open] { border-color:var(--accent-glow); }
.faq-question { padding:18px 24px; font-weight:700; font-size:.98rem; cursor:pointer; list-style:none; display:flex; justify-content:space-between; align-items:center; gap:12px; color:var(--text-primary); }
.faq-question::-webkit-details-marker { display:none; }
.faq-question::after { content:'＋'; font-size:1.2rem; font-weight:400; color:var(--text-muted); flex-shrink:0; transition:.3s; }
.faq-item[open] .faq-question::after { content:'－'; color:var(--accent); }
.faq-answer { padding:0 24px 18px; font-size:.93rem; color:var(--text-secondary); line-height:1.75; }

/* ── COMPARISON TABLE ── */
.compare-table { width:100%; border-collapse:collapse; font-size:.93rem; min-width:520px; }
.compare-table th,.compare-table td { padding:13px 18px; border-bottom:1px solid var(--border-light); text-align:center; }
.compare-table th:first-child,.compare-table td:first-child { text-align:left; color:var(--text-primary); font-weight:600; }
.compare-table thead th { background:var(--bg-elevated); font-weight:800; font-size:.88rem; text-transform:uppercase; letter-spacing:.5px; }
.compare-table thead th:last-child { background:var(--accent-soft); border:1px solid var(--accent-glow); border-radius:8px 8px 0 0; }
.compare-table tbody tr:hover { background:var(--bg-elevated); }
.ct-yes { color:var(--success); font-size:1.1rem; font-weight:900; }
.ct-no  { color:var(--text-muted); font-size:1.1rem; }
`;

const schemaData = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Do I need to be tech-savvy to use DG HelpMate?",
      "acceptedAnswer": { "@type": "Answer", "text": "Not at all. If you can type in Microsoft Word, you can use DG HelpMate. The app has a simple editor — paste your content, choose PPT or Word, click Export. The Mastery Course walks you through every step from scratch." }
    },
    {
      "@type": "Question",
      "name": "What is the difference between the App License and the Mastery Course?",
      "acceptedAnswer": { "@type": "Answer", "text": "The App License (₹999) gives you the DG HelpMate software only — you supply your own content. The Mastery Course (₹1,499) includes the app license AND a full training on using Gemini AI to generate perfect notes, PPTs, and MCQs automatically." }
    },
    {
      "@type": "Question",
      "name": "Does DG HelpMate support Math and Science equations?",
      "acceptedAnswer": { "@type": "Answer", "text": "Yes — 100% accuracy for complex equations in Physics, Chemistry, and Mathematics. The app uses a structured format that renders all symbols, fractions, and chemical formulas perfectly in the exported PPT and Word files." }
    },
    {
      "@type": "Question",
      "name": "How do I get my Activation Key after payment?",
      "acceptedAnswer": { "@type": "Answer", "text": "Your Activation Key is sent to your registered email within minutes of payment. If you do not receive it in 10 minutes, please check your spam folder or contact us on WhatsApp at +91 92416 53369." }
    },
    {
      "@type": "Question",
      "name": "Is this a one-time payment or a subscription?",
      "acceptedAnswer": { "@type": "Answer", "text": "It is a one-time payment with lifetime validity. You pay once and use the software and course materials forever. There are no monthly or annual renewal fees." }
    },
    {
      "@type": "Question",
      "name": "What subjects and languages does DG HelpMate support?",
      "acceptedAnswer": { "@type": "Answer", "text": "DG HelpMate supports all major school and coaching subjects: Physics, Chemistry, Mathematics, Biology, Hindi, English, History, Geography, and more. Content can be generated in both Hindi and English." }
    }
  ]
};

export default function App() {
  const [theme, setTheme] = useState('dark');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mockupTransform, setMockupTransform] = useState('rotateX(4deg) translateY(0)');

  // Typing animation states
  const [typedH1, setTypedH1] = useState("");
  const [typedH2, setTypedH2] = useState("");
  const [typedTxt, setTypedTxt] = useState("");
  const [progressWidth, setProgressWidth] = useState('0%');
  const [showExportBtn, setShowExportBtn] = useState(false);
  
  const heroRef = useRef(null);

  // Initialize theme
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || (window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark');
    setTheme(savedTheme);
    document.documentElement.setAttribute('data-theme', savedTheme);
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
    localStorage.setItem('theme', newTheme);
  };

  // Scroll listener
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Reveal animation intersection observer
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('active');
          observer.unobserve(e.target);
        }
      });
    }, { threshold: 0.1 });
    
    document.querySelectorAll('.reveal').forEach(el => observer.observe(el));
    
    return () => observer.disconnect();
  }, []);

  // Typing Animation Sequence
  useEffect(() => {
    let isCancelled = false;
    const texts = {
      h1: "# Chapter 4: Motion in a Plane",
      h2: "## Q1: Define uniform circular motion.",
      txt: "**Answer:** When an object follows a circular path at a constant speed, the motion is called uniform circular motion."
    };

    const runAnimation = async () => {
      while (!isCancelled) {
        setTypedH1(""); setTypedH2(""); setTypedTxt("");
        setProgressWidth('0%'); setShowExportBtn(false);

        await new Promise(r => setTimeout(r, 1000));

        for (let i = 0; i <= texts.h1.length; i++) {
          if (isCancelled) return;
          setTypedH1(texts.h1.substring(0, i));
          await new Promise(r => setTimeout(r, 35));
        }
        for (let i = 0; i <= texts.h2.length; i++) {
          if (isCancelled) return;
          setTypedH2(texts.h2.substring(0, i));
          await new Promise(r => setTimeout(r, 35));
        }
        for (let i = 0; i <= texts.txt.length; i++) {
          if (isCancelled) return;
          setTypedTxt(texts.txt.substring(0, i));
          await new Promise(r => setTimeout(r, 35));
        }

        await new Promise(r => setTimeout(r, 1000));
        setProgressWidth('100%');
        await new Promise(r => setTimeout(r, 800));
        setShowExportBtn(true);
        await new Promise(r => setTimeout(r, 4000));
      }
    };

    runAnimation();
    return () => { isCancelled = true; };
  }, []);

  // 3D Tilt effect
  const handleMouseMove = (e) => {
    if (window.innerWidth > 1024) {
      const xAxis = (window.innerWidth / 2 - e.pageX) / 50;
      const yAxis = (window.innerHeight / 2 - e.pageY) / 50;
      setMockupTransform(`rotateY(${xAxis}deg) rotateX(${yAxis}deg)`);
    }
  };

  const handleMouseLeave = () => {
    if (window.innerWidth > 1024) {
      setMockupTransform('rotateY(0deg) rotateX(4deg)');
    }
  };

  // Actions
  const handleDownloadSoftware = () => {
    setIsModalOpen(false);
    window.location.href = "https://converterdghelpmate.dghelpmate.com/download/DG-Helpmate-Setup-1.0.0.exe.zip";
  };
  const handleGoToPricing = () => {
    setIsModalOpen(false);
    window.location.href = 'https://converterdghelpmate.dghelpmate.com/payment';
  };
  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: customCSS }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />

      <section className="section hero" ref={heroRef} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
        <div className="hero-glow"></div>
        <div className="container text-center">
          <div className="badge reveal">
            <div className="pulse-dot"></div>
            The Ultimate AI Workflow for Educators
          </div>
          
          <h1 className="reveal" style={{ transitionDelay: '0.1s' }}>
            Create Smart Board PPTs <br /> 
            <span className="text-gradient relative-inline">
              Without Typing a Single Word
              <svg className="animated-curve" viewBox="0 0 100 12" preserveAspectRatio="none">
                <path d="M2,9 Q50,-2 98,9" />
              </svg>
            </span>
          </h1>
          
          <p className="reveal" style={{ transitionDelay: '0.2s' }}>
            Stop paying typists! Join the <strong>Smart Teacher Mastery Course</strong> to harness the power of Gemini AI. Instantly generate and format content for Math, Science, and Languages.
          </p>
          
          <div className="hero-buttons reveal" style={{ transitionDelay: '0.3s' }}>
            <a href="#pricing" className="btn btn-primary">
              Join the Course 
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
            </a>
            <a href="#download-section" className="btn btn-secondary">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
              Download App
            </a>
          </div>

          <div className="mockup-container reveal" style={{ transitionDelay: '0.4s' }}>
            <div className="mockup-window" style={{ transform: mockupTransform }}>
              <div className="mockup-header">
                <div className="mac-dots">
                  <div className="mac-dot r"></div><div className="mac-dot y"></div><div className="mac-dot g"></div>
                </div>
                <div className="mockup-title">
                  <div style={{ width: '18px', height: '18px', borderRadius: '4px', background: 'linear-gradient(135deg, #1a1830, #100f20)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(255,255,255,0.15)' }}>
                    <svg viewBox="0 0 186 194" width="10" height="10" xmlns="http://www.w3.org/2000/svg">
                      <path fill="#040403" d="M13.75 98.66C13.86 89.91 14.5 81.31 20.13 73.99 20.84 73.07 21.44 72.04 22.26 71.23 38.53 54.99 54.51 38.45 71.21 22.66 85.2 9.43 104.49 9.73 118.45 23.09 134.81 38.75 150.86 54.75 166.63 71.01 175.65 80.31 176.76 91.95 173.53 104.77 170.22 109.75 167.83 114.67 164.21 118.39 149.58 133.42 134.6 148.1 119.69 162.84 104.88 177.48 84.22 177.43 69.38 162.75 56.72 150.23 44.27 137.49 31.44 125.14 23.55 117.55 16.28 109.74 13.75 98.66Z"/>
                      <path fill="#F09E07" d="M13.51 98.98C16.28 109.74 23.55 117.55 31.44 125.14 44.27 137.49 56.72 150.23 69.38 162.75 84.22 177.43 104.88 177.48 119.69 162.84 134.6 148.1 149.58 133.42 164.21 118.39 167.83 114.67 170.22 109.75 173.45 105.19 175.23 116.02 170.77 124.79 163.25 132.31 148.9 146.67 134.07 160.57 120.19 175.36 105.12 191.43 80.83 190.72 66.66 175.47 52.86 160.61 38.02 146.73 23.61 132.45 14.28 123.21 10.48 112.29 13.51 98.98Z"/>
                      <path fill="#F8F8F8" d="M138.47 77.54C151.16 90.22 151.15 102.09 138.68 114.5 129.83 123.31 120.99 132.14 112.1 140.9 101.75 151.09 88.77 151.11 78.46 140.95 68.97 131.6 59.51 122.21 50.11 112.77 40.04 102.66 40.01 89.64 50.02 79.56 59.41 70.11 68.85 60.71 78.34 51.36 88.81 41.04 101.81 41.13 112.37 51.56 121.02 60.11 129.6 68.71 138.47 77.54Z"/>
                      <path fill="#030303" d="M124.87 86.23C131.37 94.12 131.2 101.42 124.45 108.25 118.24 114.54 111.98 120.76 105.66 126.92 98.74 133.66 90.35 133.74 83.5 127.09 77.04 120.82 70.67 114.47 64.36 108.06 57.83 101.4 57.71 92.89 64.15 86.28 70.54 79.71 77.04 73.25 83.63 66.88 90.26 60.47 98.74 60.5 105.46 66.96 111.94 73.2 118.23 79.63 124.87 86.23Z"/>
                    </svg>
                  </div>
                  DG HelpMate Editor
                </div>
              </div>
              <div className="mockup-body">
                <div className="mockup-sidebar">
                  <div className="sidebar-item active">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9"></path><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path></svg> Editor
                  </div>
                  <div className="sidebar-item">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path></svg> Files
                  </div>
                  <div className="sidebar-item">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="16 18 22 12 16 6"></polyline><polyline points="8 6 2 12 8 18"></polyline></svg> Snippets
                  </div>
                </div>
                <div className="mockup-main">
                  <div className="mockup-toolbar">
                    <button className="tool-btn">B</button>
                    <button className="tool-btn">I</button>
                    <div style={{ width: '1px', height: '16px', background: 'var(--border-strong)', margin: '0 8px' }}></div>
                    <button className="tool-btn">H1</button>
                    <button className="tool-btn">H2</button>
                    <button className="tool-btn" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 7V4H6v3"></path><path d="M6 20h12v-3"></path><path d="M18 4 6 20"></path></svg> Math
                    </button>
                  </div>
                  <div className="mockup-content">
                    <div className="h1">{typedH1}{typedH1.length > 0 && typedH2.length === 0 && <span className="cursor"></span>}</div>
                    <div className="h2">{typedH2}{typedH2.length > 0 && typedTxt.length === 0 && <span className="cursor"></span>}</div>
                    <div style={{ display: 'inline' }}>{typedTxt}</div>
                    {((typedH1.length === 0) || (typedTxt.length > 0 && typedTxt.length < 103)) && <span className="cursor"></span>}
                  </div>
                  <div className="mockup-footer">
                    <div className="format-toggle">
                      <div className="format-btn">Word</div>
                      <div className="format-btn active">PPT</div>
                    </div>
                    <div className="export-progress"><div className="progress-bar" style={{ width: progressWidth }}></div></div>
                    <button className={`export-btn ${showExportBtn ? 'show' : ''}`}>Exported ✓</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <FeaturedContent />

      <section className="section video-section" aria-labelledby="video-heading">
        <div className="container text-center">
          <div className="badge reveal">60-Second Demo</div>
          <h2 id="video-heading" className="reveal" style={{ fontSize: 'clamp(1.8rem,4vw,2.5rem)', fontWeight: 800, marginBottom: '12px' }}>
            Watch It Create a Full PPT <span className="text-gradient">in 60 Seconds</span>
          </h2>
          <p className="reveal" style={{ color: 'var(--text-secondary)', maxWidth: '560px', margin: '0 auto 40px', fontSize: '1rem' }}>
            No editing, no formatting, no typing. Just AI prompts and 1-click export.
          </p>
          <div className="video-wrapper reveal" style={{ aspectRatio: '16/9', width: '100%', position: 'relative' }}>
            <iframe
              width="100%"
              height="100%"
              src="https://www.youtube.com/embed/H8cDqnl1pHg?rel=0"
              title="DG HelpMate — Create a full Smart Board PPT in 60 seconds"
              frameBorder="0"
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}>
            </iframe>
          </div>
        </div>
      </section>

      <section className="section" id="comparison">
        <div className="container">
          <div className="text-center reveal">
            <div className="badge">Reality Check</div>
            <h2>
              Which Kind of 
              <span className="text-gradient relative-inline">
                {" Educator "}
                <svg className="animated-curve" viewBox="0 0 100 12" preserveAspectRatio="none">
                  <path d="M5,10 Q50,-2 95,10" />
                </svg>
              </span> 
              Are You?
            </h2>
          </div>

          <div className="compare-grid">
            <div className="compare-card bad reveal">
              <div className="compare-header">
                <div className="compare-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>
                </div>
                <div>
                  <h3 className="compare-title">Traditional Teacher</h3>
                  <span className="compare-subtitle">The Old Way</span>
                </div>
              </div>
              <ul className="compare-list">
                <li><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg> Paying typists ₹5000+ every month.</li>
                <li><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg> Waiting days for notes to be typed.</li>
                <li><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg> Thousands of formatting errors in Math/Science.</li>
                <li><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg> Boring, unengaging black & white text.</li>
              </ul>
            </div>

            <div className="compare-card good reveal" style={{ transitionDelay: '0.1s' }}>
              <div className="compare-header">
                <div className="compare-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                </div>
                <div>
                  <h3 className="compare-title">Smart Educator</h3>
                  <span className="compare-subtitle">DG HelpMate User</span>
                </div>
              </div>
              <ul className="compare-list">
                <li><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"></polyline></svg> AI generates content in 1 minute, zero typing.</li>
                <li><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"></polyline></svg> 1-click export to stunning Smart Board PPTs.</li>
                <li><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"></polyline></svg> 100% accuracy in complex Math & Science equations.</li>
                <li><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"></polyline></svg> Zero typing costs, lifetime software validity.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="section" id="features" style={{ background: 'var(--bg-elevated)' }}>
        <div className="container">
          <div className="text-center reveal">
            <div className="badge">The Complete System</div>
            <h2>Everything You Need to <span className="text-gradient">Scale Your Teaching</span></h2>
          </div>

          <div className="features-grid">
            <div className="feature-card reveal">
              <div className="feature-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="10" rx="2"></rect><circle cx="12" cy="5" r="2"></circle><path d="M12 7v4"></path><line x1="8" y1="16" x2="8" y2="16"></line><line x1="16" y1="16" x2="16" y2="16"></line></svg>
              </div>
              <h3>AI Content Creation</h3>
              <p>Learn exactly how to command AI to generate precise notes, test papers, and MCQs in our comprehensive mastery course.</p>
            </div>
            <div className="feature-card reveal" style={{ transitionDelay: '0.1s' }}>
              <div className="feature-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect><line x1="8" y1="21" x2="16" y2="21"></line><line x1="12" y1="17" x2="12" y2="21"></line></svg>
              </div>
              <h3>1-Click PPT Export</h3>
              <p>Paste the AI-generated content directly into the DG HelpMate app and export beautifully formatted Smart Board PPTs instantly.</p>
            </div>
            <div className="feature-card reveal" style={{ transitionDelay: '0.2s' }}>
              <div className="feature-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"></path></svg>
              </div>
              <h3>Secret Prompts Library</h3>
              <p>Get exclusive access to our hidden prompt sheets designed specifically for generating perfect subject-specific content.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section pricing-section" id="pricing">
        <div className="container">
          <div className="text-center reveal">
            <h2>The Smartest Investment in Your <span className="text-gradient">Career</span></h2>
            <p style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '16px auto 0' }}>Choose just the tool, or upgrade to the complete mastery system to save thousands of hours.</p>
          </div>

          <div className="pricing-grid">
            <div className="price-card reveal">
              <div className="price-header">
                <h3>App License Only</h3>
                <p>Just the desktop software. You will need to type or source your own content manually.</p>
              </div>
              <div className="price-amount">
                <span className="currency">₹</span><span className="amount">999</span>
              </div>
              <ul className="price-list">
                <li><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"></polyline></svg> DG HelpMate Software Access</li>
                <li><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"></polyline></svg> PPT & Word Export</li>
                <li className="strike"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg> AI Content Creation Training</li>
                <li className="strike"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg> Secret Prompts Library</li>
              </ul>
              <a href="https://converterdghelpmate.dghelpmate.com/payment" className="btn btn-secondary" style={{ width: '100%' }}>Get App License — ₹999</a>
            </div>

            <div className="price-card premium reveal" style={{ transitionDelay: '0.1s' }}>
              <div className="price-badge">Highly Recommended</div>
              <div className="price-header">
                <h3>Smart Teacher Mastery</h3>
                <p>The complete AI training + software bundle. Learn once, save time forever.</p>
              </div>
              <div className="price-amount">
                <span className="currency">₹</span><span className="amount">1499</span>
              </div>
              <ul className="price-list">
                <li className="highlight-item">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"></polyline></svg> FREE App License Included (Worth ₹999)
                </li>
                <li><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"></polyline></svg> <b>Full Mastery Course:</b> Create notes without typing</li>
                <li><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"></polyline></svg> <b>Secret Prompts:</b> Ready-made AI commands</li>
                <li><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"></polyline></svg> <b>Bonus:</b> YouTube Thumbnail Design Module</li>
                <li><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"></polyline></svg> VIP Priority Support</li>
              </ul>
              <a href="https://converterdghelpmate.dghelpmate.com/payment" className="btn btn-primary" style={{ width: '100%' }}>Enroll in Course Now — ₹1499</a>
            </div>
          </div>

          <div className="reveal" style={{ marginTop: '64px', overflowX: 'auto' }} role="region" aria-label="Feature comparison table">
            <table className="compare-table" aria-label="Compare App License vs Mastery Course features">
              <thead>
                <tr>
                  <th scope="col" style={{ textAlign: 'left' }}>Feature</th>
                  <th scope="col">App License<br/><span style={{ fontWeight: 500, fontSize: '.85rem', color: 'var(--text-secondary)' }}>₹999</span></th>
                  <th scope="col" style={{ color: 'var(--accent)' }}>Mastery Course<br/><span style={{ fontWeight: 500, fontSize: '.85rem' }}>₹1,499</span></th>
                </tr>
              </thead>
              <tbody>
                <tr><td>DG HelpMate Software</td><td><span className="ct-yes" aria-label="Included">✓</span></td><td><span className="ct-yes" aria-label="Included">✓</span></td></tr>
                <tr><td>PPT & Word 1-Click Export</td><td><span className="ct-yes" aria-label="Included">✓</span></td><td><span className="ct-yes" aria-label="Included">✓</span></td></tr>
                <tr><td>Lifetime Software License</td><td><span className="ct-yes" aria-label="Included">✓</span></td><td><span className="ct-yes" aria-label="Included">✓</span></td></tr>
                <tr><td>Math & Science Equation Support</td><td><span className="ct-yes" aria-label="Included">✓</span></td><td><span className="ct-yes" aria-label="Included">✓</span></td></tr>
                <tr><td>AI Content Creation Mastery Course</td><td><span className="ct-no" aria-label="Not included">✗</span></td><td><span className="ct-yes" aria-label="Included">✓</span></td></tr>
                <tr><td>100+ Secret AI Prompts Library</td><td><span className="ct-no" aria-label="Not included">✗</span></td><td><span className="ct-yes" aria-label="Included">✓</span></td></tr>
                <tr><td>YouTube Thumbnail Design Module</td><td><span className="ct-no" aria-label="Not included">✗</span></td><td><span className="ct-yes" aria-label="Included">✓</span></td></tr>
                <tr><td>VIP Priority WhatsApp Support</td><td><span className="ct-no" aria-label="Not included">✗</span></td><td><span className="ct-yes" aria-label="Included">✓</span></td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="section" id="testimonials" style={{ background: 'var(--bg-main)' }}>
        <div className="container">
          <div className="text-center reveal">
            <div className="badge">Real Teachers, Real Results</div>
            <h2>What Educators Are <span className="text-gradient">Saying</span></h2>
            <p style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '16px auto 0' }}>Join 500+ teachers who have already cut their content creation time by 90%.</p>
          </div>

          <div className="testi-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '28px', marginTop: '56px' }}>
            <article className="testi-card reveal" aria-label="Testimonial from Rajesh Kumar">
              <div className="testi-stars" aria-label="5 out of 5 stars">★★★★★</div>
              <p className="testi-quote">"Used to spend ₹3,000–₹4,000 every month on typists for my Physics notes. With DG HelpMate I now create the same quality PPTs in 10 minutes flat. Biggest ROI I have ever made."</p>
              <div className="testi-author">
                <div className="testi-avatar" aria-hidden="true">R</div>
                <div>
                  <strong className="testi-name">Rajesh Kumar</strong>
                  <span className="testi-role">Physics Teacher, Delhi</span>
                </div>
              </div>
            </article>

            <article className="testi-card reveal" style={{ transitionDelay: '.1s' }} aria-label="Testimonial from Priya Sharma">
              <div className="testi-stars" aria-label="5 out of 5 stars">★★★★★</div>
              <p className="testi-quote">"I run a coaching centre with 200+ students across 15 subjects. DG HelpMate handles all my content creation. The equation formatting for Maths and Chemistry is absolutely perfect — zero errors."</p>
              <div className="testi-author">
                <div className="testi-avatar" style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)' }} aria-hidden="true">P</div>
                <div>
                  <strong className="testi-name">Priya Sharma</strong>
                  <span className="testi-role">Coaching Centre Owner, Patna</span>
                </div>
              </div>
            </article>

            <article className="testi-card reveal" style={{ transitionDelay: '.2s' }} aria-label="Testimonial from Amit Singh">
              <div className="testi-stars" aria-label="5 out of 5 stars">★★★★★</div>
              <p className="testi-quote">"The Mastery Course taught me exactly how to prompt AI for subject-specific content. I created a full 40-slide Class 10 Science chapter PPT in under 30 minutes. My students love the quality."</p>
              <div className="testi-author">
                <div className="testi-avatar" style={{ background: 'linear-gradient(135deg,#10b981,#059669)' }} aria-hidden="true">A</div>
                <div>
                  <strong className="testi-name">Amit Singh</strong>
                  <span className="testi-role">Science Teacher, Lucknow</span>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section className="section" id="faq" style={{ background: 'var(--bg-elevated)' }}>
        <div className="container" style={{ maxWidth: '760px' }}>
          <div className="text-center reveal">
            <div className="badge">Common Questions</div>
            <h2>Frequently Asked <span className="text-gradient">Questions</span></h2>
          </div>

          <div className="faq-list" style={{ marginTop: '48px' }} role="list">
            <details className="faq-item reveal" role="listitem">
              <summary className="faq-question">Do I need to be tech-savvy to use DG HelpMate?</summary>
              <div className="faq-answer">Not at all. If you can type in Microsoft Word, you can use DG HelpMate. The app has a simple editor — paste your content, choose PPT or Word, click Export. The Mastery Course walks you through every step from scratch.</div>
            </details>

            <details className="faq-item reveal" style={{ transitionDelay: '.05s' }} role="listitem">
              <summary className="faq-question">What is the difference between the App License and the Mastery Course?</summary>
              <div className="faq-answer">The <strong>App License (₹999)</strong> gives you the DG HelpMate software only — you supply your own content. The <strong>Mastery Course (₹1,499)</strong> includes the app license AND a full training on using Gemini AI to generate perfect notes, PPTs, and MCQs automatically. Most teachers choose the Mastery Course for complete independence.</div>
            </details>

            <details className="faq-item reveal" style={{ transitionDelay: '.1s' }} role="listitem">
              <summary className="faq-question">Does DG HelpMate support Math and Science equations?</summary>
              <div className="faq-answer">Yes — 100% accuracy for complex equations in Physics, Chemistry, and Mathematics. The app uses a structured format that renders all symbols, fractions, and chemical formulas perfectly in the exported PPT and Word files.</div>
            </details>

            <details className="faq-item reveal" style={{ transitionDelay: '.15s' }} role="listitem">
              <summary className="faq-question">How do I get my Activation Key after payment?</summary>
              <div className="faq-answer">Your Activation Key is sent to your registered email within minutes of payment. If you do not receive it in 10 minutes, please check your spam folder or contact us on WhatsApp at +91 92416 53369 — we respond within 30 minutes.</div>
            </details>

            <details className="faq-item reveal" style={{ transitionDelay: '.2s' }} role="listitem">
              <summary className="faq-question">Is this a one-time payment or a subscription?</summary>
              <div className="faq-answer">It is a <strong>one-time payment with lifetime validity</strong>. You pay once and use the software and course materials forever. There are no monthly or annual renewal fees.</div>
            </details>

            <details className="faq-item reveal" style={{ transitionDelay: '.25s' }} role="listitem">
              <summary className="faq-question">What subjects and languages does it support?</summary>
              <div className="faq-answer">DG HelpMate supports all major school and coaching subjects: Physics, Chemistry, Mathematics, Biology, Hindi, English, History, Geography, and more. Content can be generated in both Hindi and English.</div>
            </details>
          </div>
        </div>
      </section>

      <section className="download-section" id="download-section">
        <div className="dl-bg-glow"></div>
        <div className="dl-container reveal">
          <div className="dl-badge">Download</div>
          <h2 className="dl-title">
            Download Now — 
            <span className="text-gradient relative-inline">
              {" Get Started "}
              <svg className="animated-curve" viewBox="0 0 100 12" preserveAspectRatio="none">
                <path d="M10,8 Q50,0 90,8" />
              </svg>
            </span>
          </h2>
          <p className="dl-subtitle">Download the software setup. An Activation Key is required to unlock export features.</p>
          
          <button onClick={() => setIsModalOpen(true)} className="dl-btn">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
            Download DG HelpMate Software
          </button>

          <div className="dl-specs" role="list" aria-label="System requirements">
            <div className="dl-spec-item" role="listitem">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect><line x1="8" y1="21" x2="16" y2="21"></line><line x1="12" y1="17" x2="12" y2="21"></line></svg>
              Windows 10 / 11
            </div>
            <div className="dl-spec-dot" aria-hidden="true"></div>
            <div className="dl-spec-item" role="listitem">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><rect x="4" y="4" width="16" height="16" rx="2"/><rect x="9" y="9" width="6" height="6"/><line x1="9" y1="1" x2="9" y2="4"/><line x1="15" y1="1" x2="15" y2="4"/><line x1="9" y1="20" x2="9" y2="23"/><line x1="15" y1="20" x2="15" y2="23"/><line x1="20" y1="9" x2="23" y2="9"/><line x1="20" y1="14" x2="23" y2="14"/><line x1="1" y1="9" x2="4" y2="9"/><line x1="1" y1="14" x2="4" y2="14"/></svg>
              Intel Core i3+ / AMD
            </div>
            <div className="dl-spec-dot" aria-hidden="true"></div>
            <div className="dl-spec-item" role="listitem">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><line x1="22" y1="12" x2="2" y2="12"></line><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"></path><line x1="6" y1="16" x2="6.01" y2="16"></line><line x1="10" y1="16" x2="10.01" y2="16"></line></svg>
              4 GB RAM minimum
            </div>
            <div className="dl-spec-dot" aria-hidden="true"></div>
            <div className="dl-spec-item" role="listitem">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path><polyline points="9 12 11 14 15 10"></polyline></svg>
              ~74 MB · Secure
            </div>
          </div>
        </div>
      </section>

      <div className={`modal ${isModalOpen ? 'active' : ''}`} role="dialog" aria-modal="true" aria-labelledby="modal-title">
        <div className="modal-box" style={{ textAlign: 'center' }}>
          <button className="close-modal" onClick={() => setIsModalOpen(false)} aria-label="Close dialog">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          </button>
          <div style={{ width: '56px', height: '56px', background: 'var(--accent-soft)', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', color: 'var(--accent)' }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
          </div>
          <h3 id="modal-title" style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '8px' }}>License Key Required</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', marginBottom: '32px' }}>To export files with DG HelpMate, you need an active license key. Do you already have one?</p>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <button onClick={handleDownloadSoftware} className="btn btn-primary" style={{ width: '100%' }}>
              Yes, I have a Key (Download)
            </button>
            <button onClick={handleGoToPricing} className="btn btn-secondary" style={{ width: '100%' }}>
              No, I need to Buy
            </button>
          </div>
        </div>
      </div>


    </>
  );
}